import React from 'react'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline'
import SmsIcon from '@material-ui/icons/Sms'
import SettingsIcon from '@material-ui/icons/Settings'
import { Link } from 'react-router-dom'

import { Campaign } from '../../../../models/Outreach/types'
import {
    getPrimaryCampaignDisplayStatus,
    PRIMARY_CAMPAIGN_STATUS_LABELS,
} from '../../utils/campaignDisplayStatus'
import { seasonCampaignDetailPath } from '../../Campaigns/utils/seasonCampaignUtils'

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    cardContent: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(2),
        '&:last-child': {
            paddingBottom: theme.spacing(2),
        },
    },
    topRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
    },
    iconWrap: {
        width: 36,
        height: 36,
        borderRadius: theme.spacing(0.75),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    emailIcon: {
        backgroundColor: '#ede9fe',
        color: '#7c3aed',
    },
    smsIcon: {
        backgroundColor: '#dbeafe',
        color: '#2563eb',
    },
    customIcon: {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.secondary,
    },
    titleBlock: {
        flex: 1,
        minWidth: 0,
    },
    title: {
        fontWeight: 600,
        fontSize: '0.875rem',
        lineHeight: 1.3,
    },
    statusChip: {
        marginTop: theme.spacing(0.75),
        height: 22,
        fontSize: '0.65rem',
        fontWeight: 600,
    },
    stats: {
        display: 'flex',
        gap: theme.spacing(2.5),
        marginBottom: theme.spacing(1.5),
    },
    statLink: {
        textDecoration: 'none',
        color: 'inherit',
        borderRadius: theme.spacing(0.5),
        padding: theme.spacing(0.25, 0.5),
        margin: theme.spacing(-0.25, -0.5),
        '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.06)',
        },
    },
    statValue: {
        fontWeight: 700,
        fontSize: '1.125rem',
        fontVariantNumeric: 'tabular-nums',
    },
    statLabel: {
        fontSize: '0.65rem',
        color: theme.palette.text.secondary,
    },
    description: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1.5),
        flex: 1,
    },
    actions: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    statusActive: {
        backgroundColor: '#ecfdf5',
        color: '#047857',
    },
    statusReady: {
        backgroundColor: '#eff6ff',
        color: '#1d4ed8',
    },
    statusSetup: {
        backgroundColor: '#fef3c7',
        color: '#b45309',
    },
}))

export type CampaignCardChannel = 'email' | 'sms' | 'mixed'

interface CampaignCardProps {
    title: string
    channel: CampaignCardChannel
    campaign?: Campaign
    seasonId?: number | null
    description?: string
    /** Always show Configure (primary system campaigns). */
    alwaysShowConfigure?: boolean
}

export const CampaignCard: React.FC<CampaignCardProps> = ({
    title,
    channel,
    campaign,
    seasonId,
    description,
    alwaysShowConfigure = false,
}) => {
    const classes = useStyles()

    const displayStatus = campaign
        ? getPrimaryCampaignDisplayStatus(campaign)
        : 'needs_setup'
    const statusLabel = PRIMARY_CAMPAIGN_STATUS_LABELS[displayStatus]
    const statusClass =
        displayStatus === 'active'
            ? classes.statusActive
            : displayStatus === 'ready_paused'
            ? classes.statusReady
            : classes.statusSetup

    const iconClass =
        channel === 'email'
            ? classes.emailIcon
            : channel === 'sms'
            ? classes.smsIcon
            : classes.customIcon

    const configureHref = campaign
        ? seasonCampaignDetailPath(campaign.id, seasonId ?? undefined)
        : undefined
    const trackingHref = campaign
        ? seasonCampaignDetailPath(
              campaign.id,
              seasonId ?? undefined,
              'tracking',
          )
        : undefined

    const defaultDescription =
        channel === 'email'
            ? 'Auto-enrolls anyone with a verified email from prospecting, your website, or calls.'
            : channel === 'sms'
            ? 'Auto-enrolls anyone with a verified phone from prospecting, your website, or calls.'
            : 'Custom outreach sequence for this season.'

    const enrolledStat = (
        <>
            <Typography className={classes.statValue}>
                {campaign?.total_prospects ?? 0}
            </Typography>
            <Typography className={classes.statLabel}>Enrolled</Typography>
        </>
    )

    return (
        <Card className={classes.card} variant="outlined">
            <CardContent className={classes.cardContent}>
                <Box className={classes.topRow}>
                    <Box className={`${classes.iconWrap} ${iconClass}`}>
                        {channel === 'sms' ? (
                            <SmsIcon fontSize="small" />
                        ) : (
                            <MailOutlineIcon fontSize="small" />
                        )}
                    </Box>
                    <Box className={classes.titleBlock}>
                        <Typography className={classes.title}>
                            {title}
                        </Typography>
                        <Chip
                            label={statusLabel}
                            size="small"
                            className={`${classes.statusChip} ${statusClass}`}
                        />
                    </Box>
                </Box>

                <Box className={classes.stats}>
                    <Box>
                        {trackingHref ? (
                            <Link
                                to={trackingHref}
                                className={classes.statLink}
                            >
                                {enrolledStat}
                            </Link>
                        ) : (
                            enrolledStat
                        )}
                    </Box>
                    <Box>
                        <Typography className={classes.statValue}>
                            {campaign?.pending_prospects ?? 0}
                        </Typography>
                        <Typography className={classes.statLabel}>
                            Pending
                        </Typography>
                    </Box>
                </Box>

                <Typography className={classes.description}>
                    {description ?? defaultDescription}
                </Typography>

                {(configureHref || alwaysShowConfigure) && (
                    <Box className={classes.actions}>
                        <Button
                            component={configureHref ? Link : 'button'}
                            to={configureHref ?? undefined}
                            variant="outlined"
                            color="primary"
                            size="small"
                            fullWidth
                            disabled={!configureHref}
                            startIcon={<SettingsIcon fontSize="small" />}
                        >
                            Configure
                        </Button>
                        {trackingHref && (
                            <Button
                                component={Link}
                                to={trackingHref}
                                variant="text"
                                color="primary"
                                size="small"
                                fullWidth
                                startIcon={
                                    <PeopleOutlineIcon fontSize="small" />
                                }
                            >
                                View tracking
                            </Button>
                        )}
                    </Box>
                )}
            </CardContent>
        </Card>
    )
}
